import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { FiDollarSign, FiCheckCircle, FiClock, FiTrendingUp } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import { getMobilizationAdvanceApi } from "../../../../Api/ClientWork/ClientWork";
import { useReportData } from "./useReportData";

const MobilizationAdvanceReport = () => {
  const gridRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const data = useReportData(getMobilizationAdvanceApi, dateRange);
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const totalAdvanceReceived = data.reduce((s, r) => s + (r.totalAdvanceReceived || 0), 0);
    const totalRecoveredPrevious = data.reduce((s, r) => s + (r.recoveredPrevious || 0), 0);
    const totalRecoveredCurrent = data.reduce((s, r) => s + (r.recoveredCurrent || 0), 0);
    const cumulativeRecovered = data.reduce((s, r) => s + (r.cumulativeRecovered || 0), 0);
    const outstandingBalance = data.reduce((s, r) => s + (r.outstandingBalance || 0), 0);
    const overallRecoveryPct =
      totalAdvanceReceived > 0
        ? ((cumulativeRecovered / totalAdvanceReceived) * 100).toFixed(1)
        : 0;

    return {
      totalAdvanceReceived,
      totalRecoveredPrevious,
      totalRecoveredCurrent,
      cumulativeRecovered,
      outstandingBalance,
      overallRecoveryPct,
    };
  }, [data]);

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Project & Client Name",
        field: "projectClientName",
        flex: 2,
        minWidth: 240,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 600, color: "var(--color-primary, #0b6462)" }}>{p.value}</span>
        ),
      },
      {
        headerName: "Total Mobilization Advance (PKR)",
        field: "totalAdvanceReceived",
        flex: 1.6,
        minWidth: 170,
        valueFormatter: (p) => (p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"),
      },
      {
        headerName: "Agreed Recovery Terms",
        field: "recoveryTerms",
        flex: 2,
        minWidth: 220,
      },
      {
        headerName: "Recovered in Previous Bills",
        field: "recoveredPrevious",
        flex: 1.4,
        minWidth: 150,
        valueFormatter: (p) => (p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"),
      },
      {
        headerName: "Recovered in Current Bill",
        field: "recoveredCurrent",
        flex: 1.4,
        minWidth: 150,
        valueFormatter: (p) => (p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"),
      },
      {
        headerName: "Cumulative Recovered to Date",
        field: "cumulativeRecovered",
        flex: 1.5,
        minWidth: 160,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: "#16a34a" }}>
            {p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"}
          </span>
        ),
      },
      {
        headerName: "Unrecovered Balance (PKR)",
        field: "outstandingBalance",
        flex: 1.5,
        minWidth: 160,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: Number(p.value) > 0 ? "#ea580c" : "#16a34a" }}>
            {p.value ? `PKR ${Number(p.value).toLocaleString()}` : "—"}
          </span>
        ),
      },
      {
        headerName: "Recovery %",
        field: "recoveryProgressPct",
        flex: 1.3,
        minWidth: 130,
        cellRenderer: (p) => {
          const pct = Number(p.value || 0);
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div
                style={{
                  width: "48px",
                  height: "7px",
                  borderRadius: "4px",
                  background: "#e2e8f0",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${Math.min(pct, 100)}%`,
                    height: "100%",
                    background: pct >= 100 ? "#16a34a" : "var(--color-primary, #0b6462)",
                  }}
                />
              </div>
              <span
                style={{
                  fontWeight: 600,
                  fontSize: "12px",
                  color: pct >= 100 ? "#16a34a" : "#0f172a",
                }}
              >
                {pct}%
              </span>
            </div>
          );
        },
      },
    ];
  }, []);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    [],
  );

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const q = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (item.projectClientName && item.projectClientName.toLowerCase().includes(q)) ||
        (item.recoveryTerms && item.recoveryTerms.toLowerCase().includes(q));

      return matchesSearch;
    });
  }, [data, searchTerm]);

  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback((field) => {
    if (!gridRef.current?.columnApi) {
      return;
    }
    const col = gridRef.current.columnApi.getColumn(field);
    if (col) {
      const isVisible = col.isVisible();
      gridRef.current.columnApi.setColumnVisible(field, !isVisible);
      setColumnVisibility((prev) => ({ ...prev, [field]: !isVisible }));
    }
  }, []);

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `Mobilization_Advance_Recovery_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("Mobilization advance report exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL ADVANCE RECEIVED"
            value={`PKR ${(kpis.totalAdvanceReceived / 1000000).toFixed(2)}M`}
            subtitle="Disbursed across awards"
            icon={FiDollarSign}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="CUMULATIVE RECOVERED"
            value={`PKR ${(kpis.cumulativeRecovered / 1000000).toFixed(2)}M`}
            subtitle={`${kpis.overallRecoveryPct}% amortized to date`}
            icon={FiCheckCircle}
            color="#16a34a"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="UNRECOVERED BALANCE"
            value={`PKR ${(kpis.outstandingBalance / 1000000).toFixed(2)}M`}
            subtitle="To recover in future IPCs"
            icon={FiClock}
            color="#f97316"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="OVERALL RECOVERY %"
            value={`${kpis.overallRecoveryPct}%`}
            subtitle="Weighted portfolio pace"
            icon={FiTrendingUp}
            color="#8b5cf6"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (General Journal style) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="Mobilization Advance Report"
            >
              Mobilization Advance Report
            </div>
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search project, client, recovery terms..."
            />

            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobilizationAdvanceReport;
